import React, { useEffect, useState } from "react";
import { api, CATEGORIES, BLOG_CATEGORIES } from "../lib/config";
import { AdminPageHeader, ImageUpload, MultiImageUpload, Field, TextInput, TextArea, Select, BtnPrimary, BtnGhost, BtnDanger } from "./AdminUI";
import { Plus, Save, Trash2, Pencil, Check, X, Mail, MailOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { GallerySortableList } from "./GallerySortableList";

/* ============ DASHBOARD ============ */
const RANGES = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "all", label: "Tout" },
];

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("30d");
  const [tab, setTab] = useState("pages"); // pages | blogs | realisations
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [bl, r, g, m] = await Promise.all([
          api.get("/admin/blog"), api.get("/reviews"), api.get("/gallery"), api.get("/admin/messages"),
        ]);
        setStats({ blog: bl.data.length, reviews: r.data.length, gallery: g.data.length, messages: m.data.filter(x => !x.read).length });
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const a = await api.get(`/admin/analytics?range=${range}`);
        setAnalytics(a.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [range]);

  const cards = [
    { label: `Visites (${RANGES.find(r => r.key === range)?.label || ""})`, v: analytics?.total ?? "—", accent: true },
    { label: "Visiteurs uniques", v: analytics?.unique_visitors ?? "—", accent: true },
    { label: "Visites aujourd'hui", v: analytics?.today ?? "—", accent: true },
    { label: "Articles de blog", v: stats?.blog ?? "—" },
    { label: "Réalisations", v: stats?.gallery ?? "—" },
    { label: "Messages non lus", v: stats?.messages ?? "—" },
  ];

  // Build daily chart from actual data (auto-fills empty days)
  const daily = analytics?.daily || [];
  const dailyMap = Object.fromEntries(daily.map(d => [d.date, d.count]));
  const nDays = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 30;
  const days = [];
  for (let i = nDays - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ date: key, label: d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }), count: dailyMap[key] || 0 });
  }
  const maxCount = Math.max(1, ...days.map(d => d.count));

  const topList = tab === "blogs" ? (analytics?.top_blogs || [])
    : tab === "realisations" ? (analytics?.top_realisations || [])
    : (analytics?.top_pages || []);
  const totalTop = topList.reduce((a, b) => a + (b.count || 0), 0) || 1;

  const sources = analytics?.sources || [];
  const totalSources = sources.reduce((a, b) => a + b.count, 0) || 1;
  const devices = analytics?.devices || [];
  const totalDevices = devices.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div data-testid="admin-dashboard">
      <AdminPageHeader
        title="Tableau de bord"
        description="Vue d'ensemble de votre site et de son trafic"
        action={
          <div className="flex items-center gap-2" data-testid="analytics-range-selector">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.1em] rounded-sm border transition-colors ${
                  range === r.key
                    ? "bg-[#9A8640] text-white border-[#9A8640]"
                    : "text-[#171717] border-[#9A8640]/40 hover:border-[#9A8640]"
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />
      <div className="p-10 space-y-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cards.map((c) => (
            <div key={c.label} className={`border p-5 ${c.accent ? "border-[#9A8640]/60 bg-[#0f0f0f]" : "border-[#9A8640]/25"}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#9A8640] mb-2">{c.label}</p>
              <p className={`font-serif text-4xl ${c.accent ? "text-[#9A8640]" : "text-white"}`}>{loading ? "…" : c.v}</p>
            </div>
          ))}
        </div>

        <div className="border border-[#9A8640]/25 p-6">
          <h2 className="font-serif text-2xl text-[#DDD4B2] mb-1">Trafic — {RANGES.find(r => r.key === range)?.label}</h2>
          <p className="text-xs text-[#DDD4B2]/60 mb-6">Visites par jour</p>
          <div className={`flex items-end gap-${nDays > 30 ? "0.5" : "2"} h-48`}>
            {days.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-2 min-w-0">
                {nDays <= 30 && <div className="text-[10px] text-[#9A8640] font-medium">{d.count > 0 ? d.count : ""}</div>}
                <div
                  className="w-full bg-[#9A8640]/30 hover:bg-[#9A8640] transition-colors cursor-pointer"
                  style={{ height: `${(d.count / maxCount) * 160 + 4}px` }}
                  title={`${d.label} : ${d.count} visites`}
                />
                {nDays <= 30 && <div className="text-[9px] text-[#DDD4B2]/60 -rotate-45 origin-top whitespace-nowrap">{d.label}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Monthly view */}
        {(analytics?.monthly?.length || 0) > 0 && (
          <div className="border border-[#9A8640]/25 p-6">
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-1">Vue mensuelle</h2>
            <p className="text-xs text-[#DDD4B2]/60 mb-6">Trafic et visiteurs uniques par mois</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#9A8640]/25">
                    <th className="text-left py-2 px-3 text-[10px] uppercase tracking-[0.15em] text-[#9A8640]">Mois</th>
                    <th className="text-right py-2 px-3 text-[10px] uppercase tracking-[0.15em] text-[#9A8640]">Pages vues</th>
                    <th className="text-right py-2 px-3 text-[10px] uppercase tracking-[0.15em] text-[#9A8640]">Visiteurs uniques</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const monthly = analytics.monthly || [];
                    const maxMonthly = Math.max(1, ...monthly.map(m => m.count));
                    return monthly.slice().reverse().map((m) => {
                      const [y, mm] = m.month.split("-");
                      const monthNames = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
                      const label = `${monthNames[parseInt(mm, 10) - 1]} ${y}`;
                      return (
                        <tr key={m.month} className="border-b border-[#9A8640]/10" data-testid={`monthly-row-${m.month}`}>
                          <td className="py-3 px-3 text-[#DDD4B2]">{label}</td>
                          <td className="py-3 px-3 text-right text-[#DDD4B2] font-medium">{m.count}</td>
                          <td className="py-3 px-3 text-right text-[#9A8640]">{m.visitors}</td>
                          <td className="py-3 px-3 w-40">
                            <div className="h-1.5 bg-[#9A8640]/10 rounded overflow-hidden">
                              <div className="h-full bg-[#9A8640]" style={{ width: `${(m.count / maxMonthly) * 100}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top content with tabs */}
        <div className="border border-[#9A8640]/25 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h3 className="font-serif text-xl text-[#DDD4B2]">Contenus les plus visités</h3>
            <div className="flex gap-1 border border-[#9A8640]/30 p-1 rounded-sm" data-testid="top-content-tabs">
              {[
                { k: "pages", l: "Toutes pages" },
                { k: "blogs", l: "Articles blog" },
                { k: "realisations", l: "Réalisations" },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className={`px-3 py-1 text-xs uppercase tracking-[0.1em] rounded-sm transition-colors ${
                    tab === t.k ? "bg-[#9A8640] text-white" : "text-[#171717] hover:bg-[#9A8640]/10"
                  }`}
                  data-testid={`tab-${t.k}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {topList.length === 0 && <p className="text-xs text-[#DDD4B2]/50">Pas encore de données sur cette période.</p>}
            {topList.map((p, i) => (
              <div key={p.path} className="text-sm py-2 border-b border-[#9A8640]/10" data-testid={`top-item-${i}`}>
                <div className="flex justify-between items-center gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-[10px] text-[#9A8640] w-6 text-center">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[#DDD4B2] truncate font-medium">{p.label}</div>
                      <a href={p.path} target="_blank" rel="noreferrer" className="text-[10px] text-[#DDD4B2]/50 hover:text-[#9A8640] truncate block">{p.path}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-24 h-1.5 bg-[#9A8640]/10 rounded overflow-hidden">
                      <div className="h-full bg-[#9A8640]" style={{ width: `${(p.count / (topList[0]?.count || 1)) * 100}%` }} />
                    </div>
                    <span className="text-[#9A8640] font-medium w-12 text-right">{p.count}</span>
                    <span className="text-[10px] text-[#DDD4B2]/50 w-10 text-right">{Math.round((p.count / totalTop) * 100)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sources + Devices */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-[#9A8640]/25 p-6">
            <h3 className="font-serif text-xl text-[#DDD4B2] mb-4">Sources de trafic</h3>
            {sources.length === 0 ? (
              <p className="text-xs text-[#DDD4B2]/50">Aucune source enregistrée pour cette période (visiteurs en accès direct).</p>
            ) : (
              <div className="space-y-2">
                {sources.map((s) => (
                  <div key={s.source} className="text-sm py-1.5">
                    <div className="flex justify-between mb-1">
                      <span className="text-[#DDD4B2]">{s.source}</span>
                      <span className="text-[#9A8640]">{s.count} <span className="text-[10px] text-[#DDD4B2]/60">({Math.round((s.count / totalSources) * 100)}%)</span></span>
                    </div>
                    <div className="w-full h-1 bg-[#9A8640]/10 rounded"><div className="h-full bg-[#9A8640]" style={{ width: `${(s.count / totalSources) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-[#9A8640]/25 p-6">
            <h3 className="font-serif text-xl text-[#DDD4B2] mb-4">Types d'appareils</h3>
            <div className="space-y-2">
              {devices.filter(d => d.count > 0).map((d) => (
                <div key={d.device} className="text-sm py-1.5">
                  <div className="flex justify-between mb-1">
                    <span className="text-[#DDD4B2]">{d.device}</span>
                    <span className="text-[#9A8640]">{d.count} <span className="text-[10px] text-[#DDD4B2]/60">({Math.round((d.count / totalDevices) * 100)}%)</span></span>
                  </div>
                  <div className="w-full h-1 bg-[#9A8640]/10 rounded"><div className="h-full bg-[#9A8640]" style={{ width: `${(d.count / totalDevices) * 100}%` }} /></div>
                </div>
              ))}
              {devices.every(d => d.count === 0) && <p className="text-xs text-[#DDD4B2]/50">Pas de données sur cette période.</p>}
            </div>
          </div>
        </div>

        <p className="text-sm text-[#DDD4B2]/70">Utilisez le menu à gauche pour gérer tous les contenus de votre site.</p>
      </div>
    </div>
  );
};

/* ============ CONTENT ============ */
export const AdminContent = () => {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => { const r = await api.get("/content"); setContent(r.data); })();
  }, []);

  const update = (key, path, value) => {
    setContent((c) => {
      const n = { ...c };
      if (!n[key]) n[key] = {};
      n[key] = { ...n[key], [path]: value };
      return n;
    });
  };

  const save = async (key) => {
    setSaving(true);
    try { await api.put(`/admin/content/${key}`, { value: content[key] }); toast.success("Sauvegardé"); }
    catch { toast.error("Erreur"); }
    setSaving(false);
  };

  const sections = [
    { key: "home_hero", title: "Accueil — Bandeau Hero", fields: [
      { path: "title", label: "Titre", type: "text" },
      { path: "subtitle", label: "Sous-titre", type: "textarea" },
      { path: "cta", label: "Texte du bouton", type: "text" },
      { path: "image", label: "Image de fond", type: "image" },
    ]},
    { key: "home_intro", title: "Accueil — Présentation", fields: [
      { path: "title", label: "Titre", type: "text" },
      { path: "text", label: "Texte", type: "textarea" },
    ]},
    { key: "atelier_story", title: "Atelier — Notre histoire", fields: [
      { path: "title", label: "Titre", type: "text" },
      { path: "text", label: "Texte", type: "textarea" },
      { path: "image", label: "Image", type: "image" },
    ]},
    { key: "contact_info", title: "Contact — Informations & réseaux sociaux", fields: [
      { path: "address", label: "Adresse", type: "text" },
      { path: "phone", label: "Téléphone", type: "text" },
      { path: "email", label: "Email", type: "text" },
      { path: "hours", label: "Horaires", type: "text" },
      { path: "instagram", label: "URL Instagram (ex: https://www.instagram.com/passionverre/)", type: "text" },
      { path: "facebook", label: "URL Facebook (ex: https://www.facebook.com/passionverreVienne)", type: "text" },
      { path: "google_business", label: "URL Google Business Profile (avis)", type: "text" },
    ]},
    { key: "google_reviews", title: "Avis Google (pour balise SEO AggregateRating)", fields: [
      { path: "rating", label: "Note moyenne (ex: 4.8) — laisser 0 pour désactiver", type: "text" },
      { path: "count", label: "Nombre d'avis (ex: 27) — laisser 0 pour désactiver", type: "text" },
    ]},
    { key: "site_announcement", title: "Pop-up d'annonce (visible à l'arrivée sur le site)", fields: [
      { path: "enabled", label: "Activer le pop-up ?", type: "checkbox", label_inline: "Cochez pour afficher l'annonce aux visiteurs" },
      { path: "title", label: "Titre du pop-up", type: "text" },
      { path: "message", label: "Message", type: "textarea" },
      { path: "cta_text", label: "Texte du bouton (ex: J'ai compris)", type: "text" },
    ]},
  ];

  return (
    <div data-testid="admin-content-page">
      <AdminPageHeader title="Contenu du site" description="Modifiez les textes et images des sections principales" />
      <div className="p-10 space-y-10 max-w-4xl">
        {sections.map((s) => (
          <div key={s.key} className="border border-[#9A8640]/25 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-[#DDD4B2]">{s.title}</h2>
              <BtnPrimary onClick={() => save(s.key)} disabled={saving} data-testid={`save-${s.key}`}><Save size={14} /> Enregistrer</BtnPrimary>
            </div>
            <div className="space-y-5">
              {s.fields.map((f) => (
                <Field key={f.path} label={f.label}>
                  {f.type === "text" && <TextInput value={content[s.key]?.[f.path] || ""} onChange={(e) => update(s.key, f.path, e.target.value)} />}
                  {f.type === "textarea" && <TextArea rows={4} value={content[s.key]?.[f.path] || ""} onChange={(e) => update(s.key, f.path, e.target.value)} />}
                  {f.type === "image" && <ImageUpload value={content[s.key]?.[f.path]} onChange={(v) => update(s.key, f.path, v)} />}
                  {f.type === "checkbox" && (
                    <label className="inline-flex items-center gap-2 text-sm text-[#DDD4B2]">
                      <input type="checkbox" checked={!!content[s.key]?.[f.path]} onChange={(e) => update(s.key, f.path, e.target.checked)} />
                      {f.label_inline}
                    </label>
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============ Generic CRUD manager ============ */
const CrudManager = ({ title, description, endpoint, fields, testid, defaultItem, renderList }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const r = await api.get(`/${endpoint}`); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/${endpoint}/${editing.id}`, editing);
      else await api.post(`/admin/${endpoint}`, editing);
      toast.success("Enregistré"); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/admin/${endpoint}/${id}`); load();
  };

  return (
    <div data-testid={testid}>
      <AdminPageHeader title={title} description={description} action={
        <BtnPrimary onClick={() => setEditing({ ...defaultItem })} data-testid={`${testid}-new`}><Plus size={14} /> Nouveau</BtnPrimary>
      } />
      {renderList ? <div className="p-10">{renderList({ items, setItems, setEditing, remove })}</div> : <div className="p-10 space-y-3">
        {items.map((it) => (
          <div key={it.id} className="border border-[#9A8640]/25 p-4 flex items-center justify-between gap-4" data-testid={`${testid}-item-${it.id}`}>
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {it.image_url && <img src={it.image_url} alt="" className="w-16 h-16 object-cover border border-[#9A8640]/20" />}
              <div className="min-w-0">
                <p className="text-[#DDD4B2] truncate">{it.title || it.name || it.question || it.author || it.id}</p>
                <p className="text-xs text-[#DDD4B2]/60 truncate">{it.description || it.text || it.answer || it.address || ""}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <BtnGhost onClick={() => setEditing(it)}><Pencil size={12} /></BtnGhost>
              <BtnDanger onClick={() => remove(it.id)}><Trash2 size={12} /></BtnDanger>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#DDD4B2]/60 text-sm">Aucun élément pour l'instant.</p>}
      </div>}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-[#171717] border border-[#9A8640]/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#9A8640]/20">
              <h3 className="font-serif text-2xl text-white">{editing.id ? "Modifier" : "Nouveau"}</h3>
              <button onClick={() => setEditing(null)} className="text-[#DDD4B2]/70"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              {fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  {f.type === "text" && <TextInput value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />}
                  {f.type === "number" && <TextInput type="number" step="any" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: parseFloat(e.target.value) || 0 })} />}
                  {f.type === "textarea" && <TextArea rows={5} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />}
                  {f.type === "image" && <ImageUpload value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />}
                  {f.type === "multi-image" && <MultiImageUpload value={editing[f.key] || []} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />}
                  {f.type === "select" && (
                    <Select value={editing[f.key] || f.options[0].value} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}>
                      {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  )}
                  {f.type === "checkbox" && (
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} />
                      {f.label_inline || ""}
                    </label>
                  )}
                </Field>
              ))}
            </div>
            <div className="p-6 border-t border-[#9A8640]/20 flex justify-end gap-3">
              <BtnGhost onClick={() => setEditing(null)}>Annuler</BtnGhost>
              <BtnPrimary onClick={save} data-testid={`${testid}-save`}><Save size={14} /> Enregistrer</BtnPrimary>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminUniverses = () => (
  <CrudManager title="Univers (Accueil)" description="Les cartes cliquables de la page d'accueil" endpoint="universes"
    testid="admin-universes"
    defaultItem={{ title: "", description: "", image_url: "", order: 0 }}
    fields={[
      { key: "title", label: "Titre", type: "text" },
      { key: "description", label: "Description courte", type: "text" },
      { key: "image_url", label: "Image", type: "image" },
      { key: "order", label: "Ordre d'affichage", type: "number" },
    ]}
  />
);

export const AdminClientLogos = () => (
  <CrudManager title="Logos clients" description="Section 'Ils nous ont fait confiance'" endpoint="client-logos"
    testid="admin-logos"
    defaultItem={{ name: "", image_url: "", order: 0 }}
    fields={[
      { key: "name", label: "Nom du client", type: "text" },
      { key: "image_url", label: "Logo", type: "image" },
      { key: "order", label: "Ordre", type: "number" },
    ]}
  />
);

export const AdminBestSellers = () => (
  <CrudManager title="Best-sellers pros" description="Les 4 best-sellers affichés sur la page Professionnels" endpoint="best-sellers"
    testid="admin-bestsellers"
    defaultItem={{ title: "", description: "", image_url: "", order: 0 }}
    fields={[
      { key: "title", label: "Titre", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image_url", label: "Image", type: "image" },
      { key: "order", label: "Ordre", type: "number" },
    ]}
  />
);

export const AdminGallery = () => {
  const [cats, setCats] = useState([]);
  const [posts, setPosts] = useState([]);
  const [enrichStatus, setEnrichStatus] = useState({ running: false, total: 0, done: 0, errors: 0, current: "" });

  useEffect(() => {
    (async () => {
      const [c, p] = await Promise.all([api.get("/gallery-categories"), api.get("/admin/blog")]);
      setCats(c.data);
      setPosts(p.data);
    })();
  }, []);

  // Poll enrich status
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/gallery/enrich-status");
        if (alive) setEnrichStatus(r.data);
      } catch {}
    };
    load();
    const id = setInterval(load, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const startEnrich = async () => {
    if (!window.confirm("Lancer l'enrichissement IA sur toutes les réalisations ? Cela peut prendre 15-30 min. Titre/description/matière/technique manquants seront complétés par l'IA (Claude Sonnet).")) return;
    try {
      const r = await api.post("/admin/gallery/enrich-all");
      toast.success(r.data?.message || "Enrichissement lancé");
    } catch (e) {
      toast.error("Erreur: " + (e.response?.data?.detail || e.message));
    }
  };

  const catOptions = cats.length ? cats.map(c => ({ value: c.slug, label: c.label })) : [{ value: "sur-mesure", label: "Sur-mesure" }];
  const blogOptions = [{ value: "", label: "— Aucun —" }, ...posts.map(p => ({ value: p.slug, label: p.title }))];

  return (
    <div>
      {/* AI enrichment control panel */}
      <div className="px-10 pt-6" data-testid="ai-enrich-panel">
        <div className="border border-[#9A8640]/40 bg-white p-5 rounded-md">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl text-[#171717] mb-1">🤖 Enrichissement IA</h3>
              <p className="text-sm text-gray-600 max-w-xl">
                Claude Sonnet analyse chaque image et complète automatiquement les titres génériques,
                descriptions, matière et technique. Les descriptions existantes riches sont conservées.
              </p>
            </div>
            {!enrichStatus.running ? (
              <button
                onClick={startEnrich}
                className="btn-gold px-5 py-2.5 text-xs uppercase tracking-[0.18em] shrink-0"
                data-testid="ai-enrich-start"
              >
                Enrichir toute la galerie
              </button>
            ) : (
              <div className="text-sm text-gray-800 min-w-[240px]" data-testid="ai-enrich-progress">
                <div className="flex justify-between mb-1">
                  <span>En cours…</span>
                  <span className="font-medium">{enrichStatus.done}/{enrichStatus.total}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-[#9A8640] transition-all"
                    style={{ width: `${enrichStatus.total ? (enrichStatus.done / enrichStatus.total * 100) : 0}%` }}
                  />
                </div>
                {enrichStatus.current && (
                  <p className="text-xs text-gray-500 mt-1 truncate">→ {enrichStatus.current}</p>
                )}
                {enrichStatus.errors > 0 && (
                  <p className="text-xs text-red-600 mt-1">⚠ {enrichStatus.errors} erreur(s)</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CrudManager title="Galerie réalisations" description="Les œuvres affichées sur la page Nos Réalisations. Toute nouvelle photo est placée automatiquement en 1re position." endpoint="gallery"
        testid="admin-gallery"
        defaultItem={{ slug: "", title: "", description: "", image_url: "", images: [], material: "", technique: "", category: catOptions[0]?.value || "sur-mesure", blog_slug: "", featured: false, order: 0 }}
        fields={[
          { key: "title", label: "Titre", type: "text" },
          { key: "slug", label: "URL (slug) — laisser vide pour auto-génération", type: "text" },
          { key: "description", label: "Description", type: "textarea" },
          { key: "image_url", label: "Image principale", type: "image" },
          { key: "images", label: "Photos supplémentaires (optionnel)", type: "multi-image" },
          { key: "material", label: "Matière (ex : verre, bois, métal)", type: "text" },
          { key: "technique", label: "Technique (ex : gravure laser, sablage)", type: "text" },
          { key: "category", label: "Catégorie", type: "select", options: catOptions },
          { key: "blog_slug", label: "Article de blog associé (optionnel)", type: "select", options: blogOptions },
          { key: "featured", label: "Réalisation phare (mise en avant)", type: "checkbox" },
        ]}
        renderList={(p) => <GallerySortableList {...p} cats={cats} />}
      />
    </div>
  );
};

export const AdminProRealizations = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { (async () => { const p = await api.get("/admin/blog"); setPosts(p.data); })(); }, []);
  const blogOptions = [{ value: "", label: "— Aucun —" }, ...posts.map(p => ({ value: p.slug, label: p.title }))];
  return (
    <CrudManager title="Réalisations professionnelles" description="Affichées sur la page Professionnels — section 'Ils nous ont fait confiance'" endpoint="pro-realizations"
      testid="admin-pro-realizations"
      defaultItem={{ title: "", description: "", image_url: "", blog_slug: "", order: 0 }}
      fields={[
        { key: "title", label: "Titre", type: "text" },
        { key: "description", label: "Description (texte court)", type: "textarea" },
        { key: "image_url", label: "Image", type: "image" },
        { key: "blog_slug", label: "Article de blog associé (optionnel)", type: "select", options: blogOptions },
        { key: "order", label: "Ordre", type: "number" },
      ]}
    />
  );
};

export const AdminGalleryCategories = () => (
  <CrudManager title="Catégories de réalisations" description="Gérez les catégories utilisées pour filtrer la galerie" endpoint="gallery-categories"
    testid="admin-categories"
    defaultItem={{ slug: "", label: "", order: 0 }}
    fields={[
      { key: "label", label: "Nom affiché (ex : Verres)", type: "text" },
      { key: "slug", label: "Slug URL (en minuscules, sans accent ni espace, ex : verres)", type: "text" },
      { key: "order", label: "Ordre d'affichage", type: "number" },
    ]}
  />
);

export const AdminBlog = () => (
  <CrudManager title="Articles de blog" description="Créez et gérez les articles du blog" endpoint="blog"
    testid="admin-blog"
    defaultItem={{ slug: "", title: "", excerpt: "", content: "", cover_image: "", images: [], category: "gravure", published: true, seo_description: "" }}
    fields={[
      { key: "title", label: "Titre", type: "text" },
      { key: "slug", label: "Slug (URL)", type: "text" },
      { key: "excerpt", label: "Extrait (texte court affiché dans la liste)", type: "textarea" },
      { key: "content", label: "Contenu complet", type: "textarea" },
      { key: "cover_image", label: "Image à la une (couverture)", type: "image" },
      { key: "images", label: "Galerie d'images supplémentaires (multi-upload, glisser pour réorganiser)", type: "multi-image" },
      { key: "seo_description", label: "Description SEO (Google) — optionnel, 150-160 caractères max", type: "textarea" },
      { key: "category", label: "Catégorie", type: "select", options: BLOG_CATEGORIES.filter(c => c.slug !== "all").map(c => ({ value: c.slug, label: c.label })) },
      { key: "published", label: "Publié ?", type: "checkbox", label_inline: "Article visible sur le site" },
    ]}
  />
);

export const AdminReviews = () => (
  <CrudManager title="Avis clients" description="Gérez les avis affichés sur l'accueil" endpoint="reviews"
    testid="admin-reviews"
    defaultItem={{ author: "", rating: 5, text: "" }}
    fields={[
      { key: "author", label: "Auteur", type: "text" },
      { key: "rating", label: "Note (1-5)", type: "number" },
      { key: "text", label: "Texte de l'avis", type: "textarea" },
    ]}
  />
);

export const AdminFAQs = () => (
  <CrudManager title="FAQ" description="Questions fréquentes" endpoint="faqs"
    testid="admin-faqs"
    defaultItem={{ question: "", answer: "", order: 0 }}
    fields={[
      { key: "question", label: "Question", type: "text" },
      { key: "answer", label: "Réponse", type: "textarea" },
      { key: "order", label: "Ordre", type: "number" },
    ]}
  />
);

export const AdminMarkers = () => (
  <CrudManager title="Carte / Graveurs partenaires" description="Marqueurs sur la carte de la page Contact" endpoint="markers"
    testid="admin-markers"
    defaultItem={{ name: "", address: "", lat: 45.4806, lng: 5.0461, products: "", website: "" }}
    fields={[
      { key: "name", label: "Nom", type: "text" },
      { key: "address", label: "Adresse", type: "text" },
      { key: "lat", label: "Latitude", type: "number" },
      { key: "lng", label: "Longitude", type: "number" },
      { key: "products", label: "Produits gravés", type: "textarea" },
      { key: "website", label: "Site web (optionnel)", type: "text" },
    ]}
  />
);

/* ============ LEGAL ============ */
export const AdminLegal = () => {
  const [slug, setSlug] = useState("mentions-legales");
  const [page, setPage] = useState({ title: "", content: "" });

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/legal/${slug}`); setPage(r.data); }
      catch { setPage({ title: "", content: "" }); }
    })();
  }, [slug]);

  const save = async () => {
    try { await api.put(`/admin/legal/${slug}`, { title: page.title, content: page.content }); toast.success("Sauvegardé"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-legal">
      <AdminPageHeader title="Pages légales" description="Mentions légales et CGV / CGU" action={
        <BtnPrimary onClick={save}><Save size={14} /> Enregistrer</BtnPrimary>
      } />
      <div className="p-10 max-w-4xl space-y-6">
        <div className="flex gap-3">
          {[{ s: "mentions-legales", l: "Mentions légales" }, { s: "cgv", l: "CGV / CGU" }].map((t) => (
            <button key={t.s} onClick={() => setSlug(t.s)}
              className={`px-5 py-2 text-xs uppercase tracking-[0.18em] border ${slug === t.s ? "bg-[#9A8640] text-[#171717] border-[#9A8640]" : "border-[#9A8640]/40 text-[#DDD4B2]"}`}
              data-testid={`legal-tab-${t.s}`}>
              {t.l}
            </button>
          ))}
        </div>
        <Field label="Titre"><TextInput value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} /></Field>
        <Field label="Contenu (Markdown — utilisez **gras** pour les titres)">
          <TextArea rows={20} value={page.content} onChange={(e) => setPage({ ...page, content: e.target.value })} />
        </Field>
      </div>
    </div>
  );
};

/* ============ FRANCHISE ============ */
export const AdminFranchise = () => {
  const [apps, setApps] = useState([]);
  const load = async () => { const r = await api.get("/admin/franchise"); setApps(r.data); };
  useEffect(() => { load(); }, []);

  const PROFILE_LABELS = {
    "graveur-existant": "Graveur existant",
    "futur-graveur": "Futur graveur",
    "curieux": "Curieux",
  };

  const markRead = async (id) => { await api.put(`/admin/franchise/${id}/read`); load(); };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/franchise/${id}`); load(); };

  return (
    <div data-testid="admin-franchise">
      <AdminPageHeader title="Candidatures franchise" description="Demandes reçues via le formulaire 'Rejoindre le réseau'" />
      <div className="p-10 space-y-4">
        {apps.length === 0 && <p className="text-[#DDD4B2]/60 text-sm">Aucune candidature pour l'instant.</p>}
        {apps.map((a) => (
          <div key={a.id} className={`border p-6 ${a.read ? "border-[#9A8640]/20 opacity-70" : "border-[#9A8640]/60"}`} data-testid={`franchise-${a.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl text-[#DDD4B2]">{a.full_name}</h3>
                  <span className="text-xs uppercase tracking-[0.18em] text-[#9A8640]">{PROFILE_LABELS[a.profile] || a.profile}</span>
                  {!a.read && <span className="text-[10px] px-2 py-0.5 bg-[#9A8640] text-[#171717] rounded-full">NOUVEAU</span>}
                </div>
                <p className="text-xs text-[#DDD4B2]/60 mt-1">{a.email}{a.phone ? ` • ${a.phone}` : ""} • {a.city} • {new Date(a.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <div className="flex gap-2">
                {!a.read && <BtnGhost onClick={() => markRead(a.id)}><Check size={12} /> Lu</BtnGhost>}
                <BtnDanger onClick={() => remove(a.id)}><Trash2 size={12} /></BtnDanger>
              </div>
            </div>
            <p className="text-[#DDD4B2]/85 whitespace-pre-wrap">{a.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============ EXPORT / IMPORT ============ */
export const AdminExportImport = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState("merge");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passionverre-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Erreur d'export"); }
    setExporting(false);
  };

  const handleImport = async () => {
    if (!file) { toast.error("Choisissez un fichier"); return; }
    if (!window.confirm(`Importer en mode ${mode === "replace" ? "REMPLACEMENT (efface tout)" : "FUSION"} ?`)) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const r = await api.post("/admin/import", { data, mode });
      setResult(r.data);
      toast.success("Import terminé");
    } catch (e) {
      toast.error("Erreur d'import : " + (e.message || ""));
    }
    setImporting(false);
  };

  return (
    <div data-testid="admin-export-import">
      <AdminPageHeader title="Migration des données" description="Exportez tout le contenu et importez-le sur un autre environnement (ex : preview → production)" />
      <div className="p-10 space-y-10 max-w-3xl">
        <div className="border border-[#9A8640]/30 p-6">
          <h2 className="font-serif text-2xl text-[#DDD4B2] mb-2">1. Exporter le contenu</h2>
          <p className="text-sm text-[#DDD4B2]/70 mb-5">Télécharge un fichier JSON contenant tous vos textes, articles, avis, FAQ, marqueurs, réalisations, logos, etc. (les images uploadées restent stockées séparément, leurs URLs sont conservées).</p>
          <BtnPrimary onClick={handleExport} disabled={exporting}>
            <Save size={14} /> {exporting ? "Export…" : "Télécharger l'export JSON"}
          </BtnPrimary>
        </div>

        <div className="border border-[#9A8640]/30 p-6">
          <h2 className="font-serif text-2xl text-[#DDD4B2] mb-2">2. Importer un fichier d'export</h2>
          <p className="text-sm text-[#DDD4B2]/70 mb-5">Sur l'autre environnement (ex : passionverre.com/admin), importez ce fichier pour transférer tout votre contenu.</p>
          <div className="space-y-4">
            <Field label="Fichier JSON à importer">
              <input type="file" accept="application/json" onChange={(e) => setFile(e.target.files?.[0])} className="text-[#DDD4B2] text-sm" />
            </Field>
            <Field label="Mode d'import">
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="mode" checked={mode === "merge"} onChange={() => setMode("merge")} className="mt-1" />
                  <div>
                    <div className="text-sm text-[#DDD4B2]">Fusion (recommandé)</div>
                    <div className="text-xs text-[#DDD4B2]/60">Met à jour les éléments existants et ajoute les nouveaux. Conserve les éléments présents qui ne sont pas dans le fichier.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="mode" checked={mode === "replace"} onChange={() => setMode("replace")} className="mt-1" />
                  <div>
                    <div className="text-sm text-red-300">Remplacement total (⚠️ destructif)</div>
                    <div className="text-xs text-[#DDD4B2]/60">Efface tout et remplace par le contenu du fichier. À utiliser avec précaution.</div>
                  </div>
                </label>
              </div>
            </Field>
            <BtnPrimary onClick={handleImport} disabled={importing || !file}>
              <Save size={14} /> {importing ? "Import…" : "Lancer l'import"}
            </BtnPrimary>
          </div>
          {result && (
            <div className="mt-6 p-4 bg-[#0f0f0f] border border-[#9A8640]/30 text-xs">
              <p className="text-[#9A8640] uppercase tracking-wider mb-2">Résultat de l'import — mode : {result.mode}</p>
              <ul className="space-y-1 text-[#DDD4B2]/85">
                {Object.entries(result.summary || {}).map(([col, s]) => (
                  <li key={col}><strong>{col}</strong> : {s.inserted} ajout(s), {s.updated} mise(s) à jour</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="text-xs text-[#DDD4B2]/60 leading-relaxed">
          ℹ️ <strong className="text-[#9A8640]">Cas d'usage typique</strong> : vous avez fait des modifications sur le preview (ex : verre-gravure-demo...), exportez le JSON ici. Puis connectez-vous au back-office de passionverre.com → Migration des données → Importez le JSON. Tous vos contenus seront transférés.
        </div>
      </div>
    </div>
  );
};

/* ============ MESSAGES ============ */
export const AdminMessages = () => {
  const [msgs, setMsgs] = useState([]);
  const load = async () => { const r = await api.get("/admin/messages"); setMsgs(r.data); };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.put(`/admin/messages/${id}/read`); load(); };
  const markUnread = async (id) => { await api.put(`/admin/messages/${id}/unread`); load(); };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/messages/${id}`); load(); };
  const resend = async (id) => {
    try {
      const r = await api.post(`/admin/messages/${id}/resend`);
      if (r.data?.ok) toast.success("Notification renvoyée à votre boîte email");
      else toast.error("Envoi échoué — vérifiez les logs");
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const buildReplyMailto = (m) => {
    const subject = encodeURIComponent(`Re: Votre demande sur Passion Verre — ${m.project_type}`);
    const orig = (m.message || "").split("\n").map(l => `> ${l}`).join("\n");
    const body = encodeURIComponent(
      `Bonjour ${m.first_name},\n\nMerci pour votre demande concernant "${m.project_type}".\n\n[Écrivez votre réponse ici]\n\nCordialement,\nAmbre — Passion Verre\ncontact@passionverre.com\n\n—————————————————————\nVotre message initial :\nNom : ${m.first_name} ${m.last_name}\nTéléphone : ${m.phone || "—"}\nType de projet : ${m.project_type}\n\n${orig}\n`
    );
    return `mailto:${m.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="admin-messages">
      <AdminPageHeader title="Messages reçus" description="Demandes envoyées via le formulaire de contact" />
      <div className="p-10 space-y-4">
        {msgs.length === 0 && <p className="text-[#DDD4B2]/60 text-sm">Aucun message pour l'instant.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`border-2 p-6 relative ${m.read ? "border-[#9A8640]/20 opacity-70" : "border-[#9A8640] bg-[#0f0f0f] shadow-lg"}`} data-testid={`message-${m.id}`}>
            {!m.read && <div className="absolute top-0 left-0 w-1.5 h-full bg-[#dc2626]" />}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  {!m.read && <span className="w-2.5 h-2.5 bg-[#dc2626] rounded-full animate-pulse" aria-label="Non lu" />}
                  <h3 className="font-serif text-xl text-[#DDD4B2]">{m.first_name} {m.last_name}</h3>
                  <span className="text-xs uppercase tracking-[0.18em] text-[#9A8640]">{m.project_type}</span>
                  {!m.read && <span className="text-[10px] px-2 py-0.5 bg-[#dc2626] text-white rounded-full font-medium tracking-wide">NON LU</span>}
                </div>
                <p className="text-xs text-[#DDD4B2]/60 mt-1">{m.email}{m.phone ? ` • ${m.phone}` : ""} • {new Date(m.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={buildReplyMailto(m)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.1em] bg-[#9A8640] text-[#171717] hover:bg-[#B99A4F] rounded-sm"
                  data-testid={`message-reply-${m.id}`}
                >
                  <Mail size={12} /> Répondre
                </a>
                <BtnGhost onClick={() => resend(m.id)} data-testid={`message-resend-${m.id}`}>
                  <RefreshCw size={12} /> Renvoyer notif
                </BtnGhost>
                {m.read ? (
                  <BtnGhost onClick={() => markUnread(m.id)} data-testid={`message-mark-unread-${m.id}`}>
                    <MailOpen size={12} /> Marquer non lu
                  </BtnGhost>
                ) : (
                  <BtnGhost onClick={() => markRead(m.id)} data-testid={`message-mark-read-${m.id}`}>
                    <Check size={12} /> Marquer lu
                  </BtnGhost>
                )}
                <BtnDanger onClick={() => remove(m.id)}><Trash2 size={12} /></BtnDanger>
              </div>
            </div>
            <p className="text-[#DDD4B2]/85 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
